/* eslint-disable import/no-named-as-default */
import { Box, Button, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR, { KeyedMutator } from 'swr';

import { Select } from '@shared/inputs/Select';
import { SERVICES } from '@shared/constants/apiConfig';
import { Loader } from '@shared/enhancers/withLoader';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { CreditReportSection, StyledCreditReportWrapper } from '@styles/CommonStyles';

import { useUserAccount } from '@shared/hooks/useUserAccount';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { TextInput } from '@shared/inputs/TextInput';
import { Roles } from '@shared/constants/constants';
import {
  CreditBureauScoreType,
  EBureauType,
  getScoreDetailsByBureau,
  IContactHeaderData,
  ICreditReportData,
} from '../interfaces';
import { NContact } from '../../Tabs/components/Contact/validation';
import { ICoApplicant } from '../../Tabs/components/Contact/components/CoApplicant/validation';

import { getCreditReportData } from '../requests';
import { generateCreditReportPdf } from '../creditBureauPrintPdfUtils';
import { AutoSummary } from './CreditReport/AutoSummary';
import { AutoSummaryProfile } from './CreditReport/AutoSummaryProfile';
import { BureauAddress } from './CreditReport/BureauAddress';
import { EmploymentDetails } from './CreditReport/EmploymentDetails';
import { Inquiry } from './CreditReport/Inquiry';
import { PersonalInfo } from './CreditReport/PersonalInfo';
import { ReportSummary } from './CreditReport/ProfileReportSummary';
import { ScoreCards } from './CreditReport/ScoreCards';
import { StudentLoan } from './CreditReport/StudentLoan';
import { TradeLines } from './CreditReport/TradeLines';
import { WarningMessage } from './CreditReport/WarningMessage';
import { FicoDetails } from './FicoDetails';
import { Collections } from './CreditReport/Collections';
import { PublicRecords } from './CreditReport/PublicRecords';
import { CreditReportMessages } from './CreditReport/CreditReportMessage';

const SCORE_PULL_ACCESS_RESTRICTED_TO_TUV4 = 'Score Pull Access Restricted to TuV4.';

interface IScoreOption {
  value: CreditBureauScoreType;
  label: string;
}

interface IDetailedCreditReportProps {
  bureauType: EBureauType;
  contactId?: string;
  mutateContactDetails?: () => void;
  reloadContact?: KeyedMutator<NContact.Entity> | KeyedMutator<ICoApplicant>;
  pulledFicoTypes: CreditBureauScoreType[];
  isViewOnly?: boolean;
  reloadDealCalculator?: () => void;
}

const getDefaultScoreType = (bureauType: EBureauType): CreditBureauScoreType | null => {
  switch (bureauType) {
    case EBureauType.Transunion:
      return CreditBureauScoreType.TuV4;

    case EBureauType.Equifax:
      return CreditBureauScoreType.EqfV9;

    case EBureauType.Experian:
      return CreditBureauScoreType.ExpV4;

    default:
      return null;
  }
};

const getFormattedBureauType = (bureauType: EBureauType, scoreType: CreditBureauScoreType | null): EBureauType => {
  if (bureauType === EBureauType.Experian) {
    return scoreType === CreditBureauScoreType.ExpV4 ? EBureauType.Experian : EBureauType.ExperianFico;
  }

  return bureauType;
};

export const DetailedCreditReport = ({
  bureauType,
  contactId,
  reloadContact,
  pulledFicoTypes,
  isViewOnly = false,
  reloadDealCalculator,
}: IDetailedCreditReportProps) => {
  const { isAdmin ,userHasRoles} = useUserAccount();
  const [creditReportData, setCreditReportData] = useState<ICreditReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedScoreType, setSelectedScoreType] = useState<CreditBureauScoreType | null>(null);

  const [scoreTypeOptions, setScoreTypeOptions] = useState<IScoreOption[]>([]);
  const [openAlert, setOpenAlert] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const enablePrint = useMemo(
    () => isAdmin || userHasRoles([Roles.LP, Roles.LP_Manager, Roles.LP_Lead, Roles.BANK, Roles.BANK_MANAGER, Roles.LC_MANAGER]),
    [isAdmin, userHasRoles]
  );

  const getData = useCallback(
    async (selectedScore: CreditBureauScoreType | null, pullLatestVersion = false) => {
      setLoading(true);
      let scoreType: CreditBureauScoreType | null;
      if (typeof selectedScore === 'string') {
        scoreType = CreditBureauScoreType[selectedScore as unknown as keyof typeof CreditBureauScoreType];
      } else if (selectedScore) {
        scoreType = selectedScore;
      } else {
        scoreType = getDefaultScoreType(bureauType);
      }

      setSelectedScoreType(scoreType);
      const formattedBureauType = getFormattedBureauType(bureauType, scoreType);
      const response = await getCreditReportData(contactId ?? '', formattedBureauType, scoreType, pullLatestVersion);
      if (response?.message == SCORE_PULL_ACCESS_RESTRICTED_TO_TUV4 && pullLatestVersion) {
        setOpenAlert(true);
      }
      setCreditReportData(response ?? null);
      setLoading(false);
    },
    [contactId, bureauType]
  );

  useEffect(() => {
    const defaultScoreType = getDefaultScoreType(bureauType);

    // If the default score type is among the pulled FICO types, select it;
    // otherwise, fall back to the previously pulled score type.
    const experianFicoType: CreditBureauScoreType | null =
      pulledFicoTypes.find(
        (ficoType) => CreditBureauScoreType[ficoType as unknown as keyof typeof CreditBureauScoreType] === defaultScoreType
      ) ??
      pulledFicoTypes[0] ??
      null;
    setSelectedScoreType(experianFicoType);
    getData(experianFicoType);

    const options = getScoreDetailsByBureau(bureauType).map((scoreDetail) => ({
      value: scoreDetail.id,
      label: scoreDetail.label,
    }));

    setScoreTypeOptions(options);
  }, [pulledFicoTypes, bureauType, getData]);

  const { mutate: mutateContactDetails } = useSWR<IContactHeaderData>(
    contactId && `/${SERVICES.CONTACT}/contacts/${contactId}/header`,
    {
      revalidateOnMount: false,
    }
  );

  const componentRef = useRef<HTMLDivElement>(null);

  const handleFetchReport = useCallback(async (score: CreditBureauScoreType | null) => {
    await getData(score, false);
    setSelectedScoreType(score);
  }, []);

  const handlePull = useCallback(async () => {
    if (!selectedScoreType) {
      Notistack.generalError('Please select score type to pull');
      return;
    }
    await getData(selectedScoreType, true);
    await mutateContactDetails();
    reloadContact && reloadContact();
    reloadDealCalculator && reloadDealCalculator();
  }, [selectedScoreType]);

  const canPull = useMemo(() => {
    if (!loading) {
      if (isAdmin) {
        return true;
      }

      return Boolean(creditReportData?.canPullCreditReport);
    }

    return false;
  }, [isAdmin, creditReportData?.canPullCreditReport, loading]);

  const handlePrintPdf = useCallback(() => {
    if (!creditReportData || !enablePrint) return;

    setIsPrinting(true);

    try {
      generateCreditReportPdf({
        creditReportData,
        bureauType,
      });
    } catch {
      Notistack.generalError('Failed to generate PDF');
    } finally {
      setIsPrinting(false);
    }
  }, [creditReportData, bureauType, enablePrint]);

  return (
    <StyledCreditReportWrapper>
      {canPull && (
        <Grid container>
          <Grid
            item
            md={3}
          >
            {isViewOnly ? (
              <TextInput
                label="Score Type"
                disabled={true}
                value={selectedScoreType !== null ? CreditBureauScoreType[selectedScoreType] : ''}
              />
            ) : (
              <Select
                hideNoneOption
                label="Select Score Type"
                value={selectedScoreType}
                options={scoreTypeOptions}
                onChange={(score: number) => handleFetchReport(score)}
              />
            )}
          </Grid>
          {isViewOnly ? (
            <Grid
              item
              md={3}
              sx={{ ml: 1 }}
            >
              <Button
                variant="contained"
                size="small"
                disabled={creditReportData?.isHardPullReport}
                onClick={handlePull}
              >
                Pull
              </Button>
            </Grid>
          ) : (
            <>
              <Grid
                item
                md={3}
              >
                <Button
                  variant="contained"
                  size="small"
                  disabled={creditReportData?.isHardPullReport}
                  onClick={handlePull}
                >
                  {bureauType === EBureauType.Transunion ? 'Soft Pull' : 'Pull'}
                </Button>
              </Grid>
            </>
          )}
          {enablePrint && (
            <Grid
              item
              marginLeft="auto"
            >
              <Button
                variant="outlined"
                size="small"
                disabled={isPrinting || !enablePrint}
                onClick={handlePrintPdf}
              >
                {isPrinting ? 'Generating PDF...' : 'Print PDF'}
              </Button>
            </Grid>
          )}
        </Grid>
      )}

      {loading ? (
        <Loader color="inherit" />
      ) : creditReportData ? (
        creditReportData.canViewCreditReport || creditReportData.canPullCreditReport ? (
          <>
            <Box ref={componentRef}>
              <Typography
                variant="h3"
                style={{ margin: '0px auto', width: '20%' }}
              >
                {bureauType} {creditReportData?.isHardPullReport ? 'Hard Pull ' : ''}Report
              </Typography>

              {creditReportData?.creditReportSummary?.personalInformation?.name}
              <FicoDetails ficoDetails={creditReportData?.creditReportSummary?.ficoDetails} />
              <Box>{creditReportData?.creditReportSummary?.mlaInformation}</Box>
              <CreditReportSection>
                <BureauAddress bureauTypeName={bureauType} />
                Date Reported: {creditReportData?.creditReportSummary?.dateReported}
              </CreditReportSection>

              <PersonalInfo personalInformation={creditReportData?.creditReportSummary?.personalInformation} />

              <EmploymentDetails employmentInfo={creditReportData?.creditReportSummary?.employmentInformation} />

              <AutoSummaryProfile summary={creditReportData?.creditReportSummary?.autoSummaryProfile} />

              <AutoSummary loan={creditReportData?.creditReportSummary?.autoSummaryProfile?.autoSummaryProfileDetails} />

              <StudentLoan
                loan={creditReportData?.creditReportSummary?.studentLoanSummaryProfile?.studentLoanSummaryProfileDetails}
              />

              <ReportSummary summary={creditReportData?.creditReportSummary?.profileReportSummary} />

              <Collections collections={creditReportData?.creditReportSummary?.collections} />

              <ScoreCards scoreCards={creditReportData?.creditReportSummary?.scoreCards} />

              <CreditReportMessages creditReportMessages={creditReportData?.creditReportSummary?.creditReportMessages} />

              <PublicRecords publicRecords={creditReportData?.creditReportSummary?.publicRecords} />

              <TradeLines
                tradeLines={creditReportData?.creditReportSummary?.tradeLines}
                bureauType={bureauType}
              />

              <Inquiry inquiries={creditReportData?.creditReportSummary?.inquiries} />

              <WarningMessage messages={creditReportData?.creditReportSummary?.warningMessage?.messages} />
            </Box>
          </>
        ) : (
          <Typography m="10px 0 0 40px">{creditReportData?.message ?? ''}</Typography>
        )
      ) : (
        <Typography m="10px 0 0 40px">No Information found</Typography>
      )}
      <AlertModal
        hideCancel
        title="Alert"
        buttonText="Ok"
        marginTop={0}
        subtitle={SCORE_PULL_ACCESS_RESTRICTED_TO_TUV4}
        open={openAlert}
        onConfirm={() => setOpenAlert(false)}
        onClose={() => setOpenAlert(false)}
      />
    </StyledCreditReportWrapper>
  );
};
